// src/pages/FileManager.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  LinearProgress,
  Divider,
  Paper,
  Stack,
  Container,
  Tooltip,
  FormControl,
  Select,
  CircularProgress
} from '@mui/material';
import {
  Upload,
  FolderPlus,
  Download,
  Trash2,
  Edit3,
  File,
  Folder,
  Image,
  FileText,
  Archive,
  Ellipsis as MoreVert,
  Home,
  HardDrive,
  RefreshCw,
  CloudUpload,
  FolderOpen,
  ArrowLeft,
  Edit,
  Save,
  SquareX,
  Code,
  Maximize,
  Check,
  TriangleAlert as Warning
} from 'lucide-react';
import api from '../services/api';

// Componente TextEditor
const TextEditor = ({ open, onClose, file, onSave }) => {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [language, setLanguage] = useState('text');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [validationError, setValidationError] = useState('');

  // Detectar linguagem baseada na extensão
  const detectLanguage = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    const languageMap = {
      'json': 'json', 'js': 'javascript', 'jsx': 'javascript',
      'ts': 'typescript', 'tsx': 'typescript', 'html': 'html',
      'css': 'css', 'md': 'markdown', 'py': 'python',
      'yaml': 'yaml', 'yml': 'yaml', 'txt': 'text'
    };
    return languageMap[ext] || 'text';
  };

  // Validar conteúdo
  const validateContent = (content, lang) => {
    if (lang === 'json' && content.trim()) {
      try {
        JSON.parse(content);
        return null;
      } catch (error) {
        return `JSON inválido: ${error.message}`;
      }
    }
    return null;
  };

  // Carregar conteúdo do arquivo
  const loadFileContent = async () => {
    if (!file) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/files/content/${file.path}`);
      const fileContent = response.data.content || '';
      
      setContent(fileContent);
      setOriginalContent(fileContent);
      setLanguage(detectLanguage(file.name));
      setHasChanges(false);
      setValidationError('');
    } catch (error) {
      showSnackbar('Erro ao carregar arquivo: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Salvar arquivo
  const handleSave = async () => {
    if (!file || !hasChanges) return;

    const error = validateContent(content, language);
    if (error) {
      setValidationError(error);
      showSnackbar('Corrija os erros antes de salvar', 'error');
      return;
    }

    try {
      setSaving(true);
      await api.put(`/api/files/content/${file.path}`, { content });

      setOriginalContent(content);
      setHasChanges(false);
      setValidationError('');
      showSnackbar('Arquivo salvo com sucesso!', 'success');
      
      if (onSave) onSave(file);
    } catch (error) {
      showSnackbar('Erro ao salvar: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Formatar JSON
  const formatContent = () => {
    if (language === 'json') {
      try {
        const parsed = JSON.parse(content);
        const formatted = JSON.stringify(parsed, null, 2);
        setContent(formatted);
        setValidationError('');
        showSnackbar('JSON formatado!', 'success');
      } catch (error) {
        showSnackbar('Erro ao formatar JSON: ' + error.message, 'error');
      }
    }
  };

  // Download do conteúdo
  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file?.name || 'file.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showSnackbar('Download iniciado!', 'success');
  };

  // Reset mudanças
  const handleReset = () => {
    if (window.confirm('Descartar todas as mudanças?')) {
      setContent(originalContent);
      setHasChanges(false);
      setValidationError('');
      showSnackbar('Mudanças descartadas', 'info');
    }
  };

  // Fechar editor
  const handleClose = () => {
    if (hasChanges && !window.confirm('Você tem mudanças não salvas. Descartar?')) {
      return;
    }
    onClose();
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Monitorar mudanças
  useEffect(() => {
    setHasChanges(content !== originalContent);
    if (content) {
      const error = validateContent(content, language);
      setValidationError(error || '');
    }
  }, [content, originalContent, language]);

  // Carregar arquivo quando abrir
  useEffect(() => {
    if (open && file) {
      loadFileContent();
    }
  }, [open, file]);

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'F11') {
        e.preventDefault();
        setFullscreen(!fullscreen);
      } else if (e.key === 'Escape' && !hasChanges) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, hasChanges, fullscreen]);

  const stats = {
    lines: content.split('\n').length,
    chars: content.length
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullScreen={fullscreen}
        PaperProps={{
          sx: {
            width: fullscreen ? '100%' : '90vw',
            height: fullscreen ? '100%' : '85vh',
            maxWidth: fullscreen ? 'none' : '1200px',
            borderRadius: fullscreen ? 0 : 3,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ p: 0, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
              <FileText size={20} color="#3b82f6" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {file?.name || 'Editor de Texto'}
              </Typography>
              
              {hasChanges && (
                <Chip label="Modificado" size="small" color="warning" sx={{ ml: 1 }} />
              )}
              
              {validationError && (
                <Chip 
                  label="Erro" 
                  size="small" 
                  color="error"
                  icon={<Warning size={14} />}
                  sx={{ ml: 1 }}
                />
              )}

              {language === 'json' && !validationError && content && (
                <Chip 
                  label="JSON Válido" 
                  size="small" 
                  color="success"
                  icon={<Check size={14} />}
                  sx={{ ml: 1 }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="text">Texto</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="javascript">JavaScript</MenuItem>
                  <MenuItem value="html">HTML</MenuItem>
                  <MenuItem value="css">CSS</MenuItem>
                  <MenuItem value="markdown">Markdown</MenuItem>
                  <MenuItem value="python">Python</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {language === 'json' && (
                  <Tooltip title="Formatar JSON">
                    <IconButton onClick={formatContent} disabled={!content || validationError}>
                      <Code size={20} />
                    </IconButton>
                  </Tooltip>
                )}
                
                <Tooltip title="Download">
                  <IconButton onClick={handleDownload}>
                    <Download size={20} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Resetar">
                  <IconButton onClick={handleReset} disabled={!hasChanges}>
                    <RefreshCw size={20} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={fullscreen ? "Sair da tela cheia" : "Tela cheia"}>
                  <IconButton onClick={() => setFullscreen(!fullscreen)}>
                    <Maximize size={20} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Fechar">
                  <IconButton onClick={handleClose}>
                    <SquareX size={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          
          {loading && <LinearProgress />}
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {validationError && (
            <Alert severity="error" sx={{ mx: 3, mt: 2, borderRadius: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
            <Box
              component="textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={loading}
              sx={{
                flexGrow: 1,
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 2,
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: '14px',
                lineHeight: 1.5,
                resize: 'none',
                outline: 'none',
                bgcolor: language === 'json' ? '#f8fafc' : 'white',
                color: '#1e293b',
                '&:focus': {
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                }
              }}
              placeholder={loading ? 'Carregando...' : 'Digite seu conteúdo aqui...'}
              spellCheck={false}
            />
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ borderTop: '1px solid #e2e8f0', p: 3, gap: 2 }}>
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Linhas: {stats.lines} | Caracteres: {stats.chars}
            </Typography>
          </Box>
          
          <Button onClick={handleClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Cancelar
          </Button>
          
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!hasChanges || saving || !!validationError}
            startIcon={saving ? undefined : <Save size={16} />}
            sx={{ borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity} 
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// Componente principal FileManager
const FileManager = () => {
  // Estados
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Dialogs
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  
  // Estados temporários
  const [folderName, setFolderName] = useState('');
  const [renameName, setRenameName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  
  // Feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Refs
  const fileInputRef = useRef(null);

  // Verificar se arquivo é editável
  const isEditableFile = (file) => {
    if (file.is_directory) return false;
    
    const editableExtensions = [
      '.txt', '.json', '.js', '.jsx', '.ts', '.tsx', '.html', '.htm',
      '.css', '.scss', '.sass', '.xml', '.yaml', '.yml', '.md',
      '.py', '.java', '.cpp', '.c', '.php', '.rb', '.go', '.sql',
      '.sh', '.bat', '.ini', '.conf', '.cfg', '.log'
    ];
    
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    return editableExtensions.includes(ext);
  };

  // Abrir editor
  const openEditor = (file) => {
    setFileToEdit(file);
    setEditorOpen(true);
  };

  // Fechar editor
  const closeEditor = () => {
    setEditorOpen(false);
    setFileToEdit(null);
  };

  // Callback quando salvar no editor
  const handleEditorSave = (file) => {
    loadFiles(currentPath);
    loadStats();
  };

  // Ícones por tipo de arquivo
  const getFileIcon = (file) => {
    if (file.is_directory) return <Folder size={20} color="#f59e0b" />;
    
    switch (file.icon) {
      case 'image': return <Image size={20} color="#3b82f6" />;
      case 'file-text': return <FileText size={20} color="#ef4444" />;
      case 'archive': return <Archive size={20} color="#8b5cf6" />;
      default: return <File size={20} color="#10b981" />;
    }
  };

  // Carregar arquivos
  const loadFiles = async (path = '') => {
    try {
      setLoading(true);
      const response = await api.get('/api/files', { params: { path } });
      setFiles(response.data.items || []);
      setCurrentPath(response.data.current_path || '');
    } catch (error) {
      showSnackbar('Erro ao carregar arquivos: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas
  const loadStats = async () => {
    try {
      const response = await api.get('/api/files/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Navegação
  const navigateToPath = (path) => {
    setCurrentPath(path);
    loadFiles(path);
  };

  const navigateUp = () => {
    if (currentPath) {
      const pathParts = currentPath.split('/').filter(p => p);
      pathParts.pop();
      const newPath = pathParts.join('/');
      navigateToPath(newPath);
    }
  };

  // Upload de arquivo
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', currentPath);

      await api.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      showSnackbar('Arquivo enviado com sucesso!', 'success');
      loadFiles(currentPath);
      loadStats();
    } catch (error) {
      showSnackbar('Erro no upload: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Criar pasta
  const handleCreateFolder = async () => {
    if (!folderName.trim()) return;

    try {
      await api.post('/api/files/create-folder', {
        name: folderName,
        path: currentPath
      });

      showSnackbar('Pasta criada com sucesso!', 'success');
      setCreateFolderOpen(false);
      setFolderName('');
      loadFiles(currentPath);
    } catch (error) {
      showSnackbar('Erro ao criar pasta: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  // Renomear
  const handleRename = async () => {
    if (!renameName.trim() || !selectedFile) return;

    try {
      await api.put('/api/files/rename', {
        path: selectedFile.path,
        new_name: renameName
      });

      showSnackbar('Renomeado com sucesso!', 'success');
      setRenameOpen(false);
      setRenameName('');
      setSelectedFile(null);
      loadFiles(currentPath);
    } catch (error) {
      showSnackbar('Erro ao renomear: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  // Deletar
  const handleDelete = async () => {
    if (!selectedFile) return;

    try {
      await api.delete('/api/files/delete', {
        data: { path: selectedFile.path }
      });

      showSnackbar('Deletado com sucesso!', 'success');
      setDeleteConfirmOpen(false);
      setSelectedFile(null);
      loadFiles(currentPath);
      loadStats();
    } catch (error) {
      showSnackbar('Erro ao deletar: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  // Download
  const handleDownload = async (file) => {
    if (file.is_directory) return;

    try {
      const response = await api.get(`/api/files/download/${file.path}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('Download iniciado!', 'success');
    } catch (error) {
      showSnackbar('Erro no download: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  // Context menu
  const handleContextMenu = (event, file) => {
    event.preventDefault();
    setSelectedFile(file);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  // Snackbar
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Breadcrumbs
  const getBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(p => p);
    const breadcrumbs = [
      { label: 'Cloud Storage', path: '' }
    ];

    let buildPath = '';
    for (const part of parts) {
      buildPath += (buildPath ? '/' : '') + part;
      breadcrumbs.push({ label: part, path: buildPath });
    }

    return breadcrumbs;
  };

  // Efeitos - DEVEM VIR ANTES DE QUALQUER RETURN CONDICIONAL
  useEffect(() => {
    loadFiles();
    loadStats();
  }, []);

  // Loading screen
  if (loading && files.length === 0) {
    return (
      <Box sx={{ 
        position: 'fixed',
        top: '64px',
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} sx={{ color: '#3b82f6' }} />
          <Typography variant="h6" sx={{ color: '#64748b' }}>
            Loading Files...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: '#f8fafc',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch'
    }}>
      <Container 
        maxWidth="lg" 
        sx={{ 
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 4 },
          pb: { xs: 8, md: 4 },
          minHeight: '100%'
        }}
      >
        {/* Header */}
        <Box sx={{ 
          mb: 4,
          pt: '80px' // Space for navbar on both mobile and desktop
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: { xs: 'flex-start', md: 'center' }, 
            justifyContent: 'space-between',
            mb: 3,
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 0 }
          }}>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontSize: { xs: '1.5rem', md: '2.125rem' }
                }}
              >
                <HardDrive size={32} color="#3b82f6" />
                File Manager
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748b',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Manage your files and folders in the cloud
              </Typography>
            </Box>

            {/* Ações principais */}
            <Button 
              variant="outlined" 
              startIcon={<RefreshCw size={18} />} 
              onClick={() => { loadFiles(currentPath); loadStats(); }}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                width: { xs: '100%', md: 'auto' }
              }}
            >
              Refresh
            </Button>
          </Box>

          {/* Card de estatísticas e toolbar */}
          <Card 
            elevation={0}
            sx={{ 
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Estatísticas */}
              {stats && (
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                      color: '#374151'
                    }}
                  >
                    Storage Overview
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f1f5f9', 
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                          {stats.total_size_human}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Storage
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f0fdf4', 
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                          {stats.total_files}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Files
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#fef3c7', 
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                          {stats.total_folders}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Folders
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f3e8ff', 
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                          {files.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current Items
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Toolbar */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                alignItems: { xs: 'stretch', md: 'center' },
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' }
              }}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  
                  <Button
                    variant="contained"
                    startIcon={uploading ? <CloudUpload size={16} /> : <Upload size={16} />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: 'none',
                      '&:hover': {
                        boxShadow: theme => theme.shadows[4]
                      }
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<FolderPlus size={16} />}
                    onClick={() => setCreateFolderOpen(true)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    New Folder
                  </Button>
                </Stack>

                {currentPath && (
                  <Button
                    variant="outlined"
                    startIcon={<ArrowLeft size={16} />}
                    onClick={navigateUp}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      width: { xs: '100%', md: 'auto' }
                    }}
                  >
                    Back
                  </Button>
                )}
              </Box>

              {uploading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress sx={{ borderRadius: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Uploading file...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Card 
            elevation={0}
            sx={{ 
              border: '1px solid #e2e8f0',
              borderRadius: 2
            }}
          >
            <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
              <Breadcrumbs 
                separator="/"
                sx={{
                  '& .MuiBreadcrumbs-separator': {
                    color: '#94a3b8',
                    mx: 1
                  }
                }}
              >
                {getBreadcrumbs().map((breadcrumb, index) => (
                  <Link
                    key={index}
                    component="button"
                    variant="body2"
                    onClick={() => navigateToPath(breadcrumb.path)}
                    sx={{ 
                      textDecoration: 'none',
                      color: index === getBreadcrumbs().length - 1 ? '#1e293b' : '#64748b',
                      fontWeight: index === getBreadcrumbs().length - 1 ? 600 : 500,
                      '&:hover': {
                        color: '#3b82f6'
                      },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {index === 0 && <Home size={16} />}
                    {index === getBreadcrumbs().length - 1 && index > 0 && <FolderOpen size={16} />}
                    {breadcrumb.label}
                  </Link>
                ))}
              </Breadcrumbs>
            </CardContent>
          </Card>
        </Box>

        {/* Lista de arquivos */}
        <Card 
          elevation={0}
          sx={{ 
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {loading && files.length === 0 && (
              <Box sx={{ p: 3 }}>
                <LinearProgress sx={{ borderRadius: 1 }} />
              </Box>
            )}
            
            {files.length === 0 && !loading ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <FolderOpen size={80} color="#cbd5e1" />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mt: 3,
                    fontWeight: 600,
                    color: '#64748b'
                  }}
                >
                  This folder is empty
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1,
                    color: '#94a3b8'
                  }}
                >
                  Upload files or create a new folder to get started
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {files.map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Paper
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          border: '1px solid #e2e8f0',
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': { 
                            bgcolor: '#f8fafc',
                            transform: 'translateY(-2px)',
                            boxShadow: theme => theme.shadows[4],
                            borderColor: '#cbd5e1'
                          }
                        }}
                        onDoubleClick={() => {
                          if (file.is_directory) {
                            navigateToPath(file.path);
                          } else if (isEditableFile(file)) {
                            openEditor(file);
                          } else {
                            handleDownload(file);
                          }
                        }}
                        onContextMenu={(e) => handleContextMenu(e, file)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                          <Box sx={{ mt: 0.25 }}>
                            {getFileIcon(file)}
                          </Box>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                wordBreak: 'break-word',
                                color: '#1e293b',
                                lineHeight: 1.3
                              }}
                            >
                              {file.name}
                            </Typography>
                          </Box>

                          {/* Botão de editar se for arquivo editável */}
                          {isEditableFile(file) && (
                            <Tooltip title="Edit file">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditor(file);
                                }}
                                sx={{ 
                                  opacity: 0.7,
                                  '&:hover': { opacity: 1 }
                                }}
                              >
                                <Edit size={16} />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="More options">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleContextMenu(e, file);
                              }}
                              sx={{ 
                                opacity: 0.7,
                                '&:hover': { opacity: 1 }
                              }}
                            >
                              <MoreVert size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Chip 
                            label={file.type} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              borderRadius: 1.5,
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                            {file.size_human}
                          </Typography>
                        </Box>
                        
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#94a3b8',
                            display: 'block',
                            mt: 0.5
                          }}
                        >
                          Modified: {file.modified}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        {selectedFile && !selectedFile.is_directory && (
          <MenuItem 
            onClick={() => { handleDownload(selectedFile); closeContextMenu(); }}
            sx={{ gap: 1.5 }}
          >
            <Download size={16} />
            Download
          </MenuItem>
        )}
        
        {/* Opção de editar */}
        {selectedFile && isEditableFile(selectedFile) && (
          <MenuItem 
            onClick={() => { 
              openEditor(selectedFile); 
              closeContextMenu(); 
            }}
            sx={{ gap: 1.5 }}
          >
            <Edit size={16} />
            Editar
          </MenuItem>
        )}
        
        <MenuItem 
          onClick={() => { 
            setRenameName(selectedFile?.name || ''); 
            setRenameOpen(true); 
            closeContextMenu(); 
          }}
          sx={{ gap: 1.5 }}
        >
          <Edit3 size={16} />
          Rename
        </MenuItem>
        
        <Divider />
        
        <MenuItem 
          onClick={() => { 
            setDeleteConfirmOpen(true); 
            closeContextMenu(); 
          }}
          sx={{ 
            gap: 1.5,
            color: 'error.main' 
          }}
        >
          <Trash2 size={16} />
          Delete
        </MenuItem>
      </Menu>

      {/* Dialog: Criar Pasta */}
      <Dialog 
        open={createFolderOpen} 
        onClose={() => setCreateFolderOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            New Folder
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            fullWidth
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setCreateFolderOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateFolder} 
            variant="contained" 
            disabled={!folderName.trim()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme => theme.shadows[4]
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Renomear */}
      <Dialog 
        open={renameOpen} 
        onClose={() => setRenameOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Rename Item
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="New name"
            fullWidth
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleRename()}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setRenameOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRename} 
            variant="contained" 
            disabled={!renameName.trim()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme => theme.shadows[4]
              }
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar Deleção */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626' }}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete "<strong>{selectedFile?.name}</strong>"?
          </Typography>
          {selectedFile?.is_directory && (
            <Alert 
              severity="warning" 
              sx={{ 
                borderRadius: 2
              }}
            >
              This action will permanently delete the folder and all its contents.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme => theme.shadows[4]
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editor de Texto */}
      <TextEditor
        open={editorOpen}
        onClose={closeEditor}
        file={fileToEdit}
        onSave={handleEditorSave}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: { xs: 24, md: 24 },
          '& .MuiSnackbarContent-root': {
            mb: { xs: 2, md: 0 }
          }
        }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            maxWidth: { xs: 'calc(100vw - 32px)', md: 'none' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileManager;